import React, { useContext, useRef, useState } from "react";

import { WhooshThemeColors } from "../../../theme/restyle/theme";
import { Box, HStack, Pressable, Text } from "../../primitive";
import { RadioGroupContext } from "./RadioGroupContext";

export type RadioProps = {
  value: string;
  label?: string;
  /**
   * Overrides the group context selection state.
   * Useful for standalone usage or Storybook demos.
   */
  isSelected?: boolean;
  isDisabled?: boolean;
  onSelect?: (value: string) => void;
  testID?: string;
  accessibilityLabel?: string;
};

export const Radio = ({
  value,
  label,
  isSelected: isSelectedProp,
  isDisabled = false,
  onSelect,
  testID,
  accessibilityLabel,
}: RadioProps) => {
  const groupContext = useContext(RadioGroupContext);

  const isSelected =
    isSelectedProp !== undefined
      ? isSelectedProp
      : groupContext?.value === value;

  const isDisabledResolved = isDisabled || (groupContext?.isDisabled ?? false);

  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const isPointerDownRef = useRef(false);

  const handlePress = () => {
    if (!isDisabledResolved) {
      onSelect?.(value);
      groupContext?.onChange(value);
    }
  };

  const circleBg: WhooshThemeColors = (() => {
    if (isDisabledResolved) return "button-disabled-surface-neutral";
    if (isSelected) {
      if (isPressed) return "button-main-surface-pressed";
      if (isHovered) return "button-main-surface-hover";
      return "button-main-surface-neutral";
    }
    if (isPressed) return "button-alt-surface-hover";
    if (isHovered) return "surface-secondary";
    return "surface-primary";
  })();

  const showBorder = !isSelected && !isDisabledResolved;

  const dotColor: WhooshThemeColors = isDisabledResolved
    ? "icon-disabled"
    : "icon-inverse";

  const textColor: WhooshThemeColors = isDisabledResolved
    ? "text-disabled"
    : "text-primary";

  const focusBorderColor: WhooshThemeColors = isFocusVisible
    ? "outline-accent-blue"
    : "transparent";

  return (
    <Pressable
      testID={testID}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="radio"
      accessibilityState={{ checked: isSelected }}
      aria-checked={isSelected}
      onPress={handlePress}
      disabled={isDisabledResolved}
      onFocus={() => setIsFocusVisible(!isPointerDownRef.current)}
      onBlur={() => setIsFocusVisible(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPressIn={() => {
        isPointerDownRef.current = true;
        setIsPressed(true);
      }}
      onPressOut={() => {
        isPointerDownRef.current = false;
        setIsPressed(false);
      }}
      style={{
        outlineColor: "transparent",
      }}
    >
      <HStack gap={2} alignItems="center">
        <Box borderWidth={2} borderColor={focusBorderColor} borderRadius="6">
          <Box
            width={16}
            height={16}
            borderRadius="6"
            borderWidth={showBorder ? 1.5 : 0}
            borderColor={showBorder ? "outline-default" : "transparent"}
            bg={circleBg}
            justifyContent="center"
            alignItems="center"
          >
            {isSelected && (
              <Box width={6} height={6} borderRadius="6" bg={dotColor} />
            )}
          </Box>
        </Box>

        {label != null && (
          <Text variant="mainBaseMedium" color={textColor} selectable={false}>
            {label}
          </Text>
        )}
      </HStack>
    </Pressable>
  );
};
